import { Color, MeshStandardMaterial, NearestFilter, RepeatWrapping, TextureLoader, Vector3 } from 'three';
import CustomShaderMaterial from 'three-custom-shader-material/vanilla';
import fragmentShader from './shaders/pattern_FRAG.glsl';
import vertexShader from './shaders/pattern_VERT.glsl';

const MAX_GEMS = 32;

/** Build a zero-filled float array of length MAX_GEMS. */
function zeroFloatArray(): number[] {
    return new Array(MAX_GEMS).fill(0);
}

/** Build a zero-filled Vector3 array of length MAX_GEMS. */
function zeroVec3Array(): Vector3[] {
    return Array.from({ length: MAX_GEMS }, () => new Vector3());
}

/** Subtle polished-metal normal map applied when no surface-specific normal is provided. */
const DEFAULT_NORMAL_MAP = '/3d/textures/PP_normal.png';

/** Circular groove channel at the ring die wall. */
export interface GrooveOptions {
    /** Ring centre-line radius in scene units */
    coreRadius: number;
    /** Z coordinate of the groove torus centre */
    z: number;
    /** Tube radius — half the channel width */
    tube: number;
}

/** Undulating emissive stripe that oscillates in Z around the ring. */
export interface WaveOptions {
    /** Z amplitude in scene units */
    amplitude: number;
    /** Number of complete oscillations per full 2π revolution */
    frequency: number;
    /** Stripe half-width in Z (scene units) */
    band: number;
}

export interface RingPatternOptions {
    color?: Color | string | number;
    roughness?: number;
    metalness?: number;
    envMapIntensity?: number;
    roughnessMapUrl?: string;
    /** Defaults to PP_normal.png; pass null to disable. */
    normalMapUrl?: string | null;
    /** Three.js hex for the emissive glow (default warm white 0xffeebb) */
    glowColor?: number;
    emissiveStrength?: number;
    groove?: GrooveOptions;
    wave?: WaveOptions;
    /** Pre-placed gem spot positions in ring local-object space, with per-gem radii. */
    gemSpots?: { positions: Vector3[]; radii: number[] };
}

/**
 * PBR metal material for the ring body with optional mathematical emissive patterns.
 *
 * Patterns are computed entirely in the fragment shader from object-space position —
 * no UV coordinates required. Supported patterns:
 *   - groove: circular torus-section channel at the die wall
 *   - wave:   sinusoidal undulating stripe around the ring
 *
 * In light mode all patterns are invisible (u_isDark = 0 → emissive = 0).
 * In dark mode they glow with glowColor, feeding the Bloom post-process.
 *
 * Use setPatternDark() / setPatternGlowColor() to update uniforms reactively.
 */
export function createRingPatternMaterial(opts: RingPatternOptions = {}): CustomShaderMaterial {
    const {
        color = '#c8bfb0',
        roughness = 0.05,
        metalness = 1.0,
        envMapIntensity = 1.5,
        roughnessMapUrl,
        normalMapUrl = DEFAULT_NORMAL_MAP,
        glowColor = 0xffeebb,
        emissiveStrength = 3.5,
        groove,
        wave,
        gemSpots,
    } = opts;

    const glowVec = new Color(glowColor);

    const mat = new CustomShaderMaterial({
        baseMaterial: MeshStandardMaterial,
        color: new Color(color),
        // emissive must be non-black so Three.js compiles the emissive code path
        emissive: glowVec.clone(),
        emissiveIntensity: 1,
        roughness,
        metalness,
        envMapIntensity,
        vertexShader,
        fragmentShader,
        silent: true,
        uniforms: {
            u_isDark:         { value: 0.0 },
            u_glowColor:      { value: glowVec },
            u_emissiveStrength: { value: emissiveStrength },
            // Groove
            u_grooveEnabled:  { value: groove ? 1.0 : 0.0 },
            u_coreRadius:     { value: groove?.coreRadius ?? 1.0 },
            u_grooveZ:        { value: groove?.z ?? 0.0 },
            u_grooveTube:     { value: groove?.tube ?? 0.05 },
            // Sine wave
            u_waveEnabled:    { value: wave ? 1.0 : 0.0 },
            u_waveAmplitude:  { value: wave?.amplitude ?? 0.1 },
            u_waveFrequency:  { value: wave?.frequency ?? 8.0 },
            u_waveBand:       { value: wave?.band ?? 0.05 },
            // Gem setting pockets (painted on ring surface)
            u_gemCount:       { value: gemSpots?.positions.length ?? 0 },
            u_gemPositions:   { value: (() => {
                const arr = zeroVec3Array();
                gemSpots?.positions.forEach((p, i) => { if (i < MAX_GEMS) arr[i].copy(p); });
                return arr;
            })() },
            u_gemRadii:       { value: (() => {
                const arr = zeroFloatArray();
                gemSpots?.radii?.forEach((r, i) => { if (i < MAX_GEMS) arr[i] = r; });
                return arr;
            })() },
        },
    });

    const loader = new TextureLoader();

    if (roughnessMapUrl) {
        loader.load(roughnessMapUrl, (tex) => {
            tex.wrapS = RepeatWrapping;
            tex.wrapT = RepeatWrapping;
            tex.minFilter = NearestFilter;
            (mat as unknown as MeshStandardMaterial).roughnessMap = tex;
            mat.needsUpdate = true;
        });
    }

    if (normalMapUrl) {
        loader.load(normalMapUrl, (tex) => {
            tex.wrapS = RepeatWrapping;
            tex.wrapT = RepeatWrapping;
            (mat as unknown as MeshStandardMaterial).normalMap = tex;
            mat.needsUpdate = true;
        });
    }

    return mat;
}

/** Imperatively set dark/light state — call on every isDark store change. */
export function setPatternDark(mat: CustomShaderMaterial, isDark: boolean): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (mat.uniforms as any).u_isDark.value = isDark ? 1.0 : 0.0;
}

/** Imperatively update glow colour — call on every glowColor store change. */
export function setPatternGlowColor(mat: CustomShaderMaterial, hex: number): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (mat.uniforms as any).u_glowColor.value.set(hex);
}

/**
 * Imperatively update gem spot positions on the ring surface.
 * Call after gem placements resolve; clears spots when positions is empty.
 *
 * @param positions  Array of gem surface points in ring local-object space.
 * @param radii      Per-gem pocket radius in scene units (gem.diameter/2 + 0.1mm).
 */
export function setGemSpots(mat: CustomShaderMaterial, positions: Vector3[], radii: number[]): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const u = mat.uniforms as any;
    const count = Math.min(positions.length, MAX_GEMS);
    u.u_gemCount.value = count;
    const posArr: Vector3[] = u.u_gemPositions.value;
    const radArr: number[] = u.u_gemRadii.value;
    for (let i = 0; i < count; i++) {
        posArr[i].copy(positions[i]);
        radArr[i] = radii[i] ?? 0.1;
    }
}
